<script>
import axios from "axios";
import Swal from "sweetalert2";

export default {
  props: {
    show: Boolean,
  },

  data() {
    return {
      BACKEND_URL_VERSION: process.env.VUE_APP_BACKEND_URL_VERSION,
      isDownloading: false,
      isLoading: false,
      form: {
        file: null,
      },
    };
  },

  watch: {
    show(val) {
      if (val) this.resetForm();
    },
  },

  computed: {
    modalShow: {
      get() {
        return this.show;
      },
      set(val) {
        if (!val) {
          if (this.isLoading) return;
          this.$emit("close");
        }
      },
    },
  },

  methods: {
    resetForm() {
      this.form.file = null;
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = null;
      }
    },

    handleFileChange(event) {
      this.form.file = event.target.files[0] ?? null;
    },

    downloadTemplate() {
      if (this.isDownloading) return;
      this.isDownloading = true;

      axios({
        method: "get",
        url:
          this.BACKEND_URL_VERSION +
          "master-asesmen/target-kompetensi/download-template",
        responseType: "blob",
        headers: {
          Authorization: "Bearer " + localStorage.access_token,
        },
      })
        .then((response) => {
          const blobUrl = window.URL.createObjectURL(
            new Blob([response.data], {
              type:
                response.headers["content-type"] ||
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            })
          );

          let fileName = "template_target_kompetensi.xlsx";
          const contentDisposition = response.headers["content-disposition"];
          if (contentDisposition) {
            const match = contentDisposition.match(
              /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i
            );
            if (match && match[1]) fileName = decodeURIComponent(match[1]);
          }

          const link = document.createElement("a");
          link.href = blobUrl;
          link.setAttribute("download", fileName);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(blobUrl);

          Swal.fire({
            icon: "success",
            title: "Berhasil",
            text: "Template berhasil diunduh",
            timer: 1500,
            timerProgressBar: true,
            showConfirmButton: false,
          });
        })
        .catch((err) => {
          Swal.fire({
            icon: "error",
            title: "Gagal",
            text:
              err.response?.data?.meta?.message || "Gagal mengunduh template.",
          });
        })
        .finally(() => {
          this.isDownloading = false;
        });
    },

    store() {
      if (this.isLoading) return;

      if (!this.form.file) {
        Swal.fire({
          icon: "warning",
          title: "Peringatan",
          text: "File template tidak boleh kosong.",
        });
        return;
      }

      const formData = new FormData();
      formData.append("file", this.form.file);

      this.isLoading = true;

      axios({
        method: "post",
        url: this.BACKEND_URL_VERSION + "master-asesmen/target-kompetensi/import",
        data: formData,
        headers: {
          Authorization: "Bearer " + localStorage.access_token,
          "Content-Type": "multipart/form-data",
        },
      })
        .then((response) => {
          const responseData = response.data?.data ?? response.data ?? {};
          const result = {
            success: responseData.success ?? [],
            skipped: responseData.skipped ?? [],
            failed: responseData.failed ?? [],
          };
          const hasIssue = result.skipped.length > 0 || result.failed.length > 0;

          Swal.fire({
            icon: "success",
            title: "Berhasil",
            text:
              response.data?.meta?.message ||
              `Import selesai. ${result.success.length} berhasil, ${result.skipped.length} dilewati, ${result.failed.length} gagal.`,
            timer: 1500,
            timerProgressBar: true,
            showConfirmButton: false,
          }).then(() => {
            if (hasIssue) this.$emit("show-failed", result);
          });

          this.resetForm();
          this.$emit("finish");
          if (!hasIssue) this.$emit("close");
        })
        .catch((err) => {
          Swal.fire({
            icon: "error",
            title: "Gagal",
            text:
              err.response?.data?.data ||
              err.response?.data?.meta?.message ||
              "Gagal mengimport data.",
          });

          this.resetForm();
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
};
</script>

<template>
  <b-modal
    v-model="modalShow"
    title="Import Target UK"
    size="md"
    hide-footer
    :no-close-on-backdrop="isLoading"
    :no-close-on-esc="isLoading"
    :hide-header-close="isLoading"
  >
    <b-form-group class="fw-medium">
      <template #label>
        <div class="d-flex justify-content-between align-items-center">
          <span>File Template</span>
          <b-button
            variant="success"
            size="sm"
            :disabled="isDownloading"
            @click="downloadTemplate"
          >
            <span v-if="isDownloading">
              <i class="fa fa-spinner fa-spin me-1"></i> Mengunduh...
            </span>
            <span v-else>
              <i class="fa fa-download me-1"></i> Download Template
            </span>
          </b-button>
        </div>
      </template>
      <input
        type="file"
        ref="fileInput"
        class="form-control"
        accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
        :disabled="isLoading"
        @change="handleFileChange"
      />
    </b-form-group>

    <div class="alert alert-warning mb-0" role="alert">
      <i class="fa fa-info-circle me-1"></i>
      Sebelum melakukan import, silahkan baca panduan pengisian pada sheet
      <strong>Panduan</strong> di dalam file template.
    </div>

    <div class="text-end mt-3">
      <b-button
        variant="secondary"
        class="me-2"
        :disabled="isLoading"
        @click="$emit('close')"
      >
        Close
      </b-button>
      <b-button variant="primary" :disabled="isLoading" @click="store">
        <span v-if="isLoading">
          <i class="fa fa-spinner fa-spin me-1"></i> Mengimport...
        </span>
        <span v-else>Import</span>
      </b-button>
    </div>
  </b-modal>
</template>
